import { type ReactNode } from 'react';

import type { HistoryState } from '@tanstack/react-router';
import { Link, useNavigate } from '@tanstack/react-router';
import { twMerge } from 'tailwind-merge';

import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/_components';
import { contentAlignmentStyle } from '@/_shared';
import type { ToPath } from '@/types';

import { CellAvatar } from './cell-avatar';
import { CellText } from './cell-text';
import type { Align } from './types';

type AvatarShape = 'circle' | 'rectangle' | 'square';

function resolveInternalPath(to: ToPath, params?: Record<string, string>): string {
  let path = String(to);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      path = path.replace(`$${key}`, value);
    }
  }
  return path;
}

type CellNameProps = {
  to?: ToPath;
  name: string | ReactNode;
  fallback?: string;
  src?: string;
  params?: Record<string, string>;
  state?: HistoryState;
  className?: string;
  avatarClassName?: string;
  showAvatar?: boolean;
  avatarShape?: AvatarShape;
  showHoverOnCard?: boolean;
  containerClassName?: string;
  align?: Align;
  target?: string;
};

export function CellName({
  to,
  src,
  name,
  fallback,
  state,
  params,
  className,
  avatarClassName,
  showAvatar = true,
  avatarShape = 'circle',
  showHoverOnCard = false,
  align = 'left',
  containerClassName,
  target,
}: CellNameProps) {
  const hasLink = params?.id && params.id !== 'undefined';
  const nav = useNavigate();

  const handleAvatarClick = () => {
    if (!hasLink || !to) {
      return;
    }
    if (target === '_blank') {
      window.open(resolveInternalPath(to, params), '_blank', 'noopener,noreferrer');
      return;
    }
    nav({ to, state, params });
  };

  const nameElement = (
    <CellText className={twMerge(!to ? '' : 'decoration-primary decoration-2 group-hover:underline', className)}>
      {name}
    </CellText>
  );

  const avatarElement = (
    <CellAvatar
      src={src}
      name={fallback || (typeof name === 'string' ? name : undefined)}
      avatarShape={avatarShape}
      className={twMerge(avatarClassName, className)}
      onClick={handleAvatarClick}
      forceShow
    />
  );

  return (
    <div className={twMerge('group flex items-center', hasLink && 'cursor-pointer', contentAlignmentStyle[align])}>
      {showAvatar && showHoverOnCard && (
        <HoverCard openDelay={150} closeDelay={150}>
          <HoverCardTrigger>{avatarElement}</HoverCardTrigger>
          <HoverCardContent
            side="top"
            className={twMerge('w-auto max-w-full px-0 py-0', avatarShape === 'circle' && 'rounded-full')}
          >
            <CellAvatar
              src={src}
              avatarShape={avatarShape}
              className={twMerge(avatarClassName, className)}
              onClick={handleAvatarClick}
              forceShow
              name={fallback || (typeof name === 'string' ? name : undefined)}
              isLarge
            />
          </HoverCardContent>
        </HoverCard>
      )}

      {showAvatar && !showHoverOnCard && avatarElement}

      {hasLink && to ? (
        <Link
          className={twMerge(showAvatar && 'pl-2', containerClassName)}
          to={to}
          state={state}
          params={params}
          target={target}
          rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        >
          {nameElement}
        </Link>
      ) : (
        <div className={twMerge(showAvatar && 'pl-2', containerClassName)}>{nameElement}</div>
      )}
    </div>
  );
}
